import { Offer } from '@n2-common/serverGeneratedTypings/offer';
import { joinNames } from '@n2-common/ui/helpers';
import Slider from 'rc-slider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import style from './style.module.scss';
import { ApplicationStatusModel } from '@n2-common/serverGeneratedTypings/applicationStatusModel';
import { useApplicationIdContext } from '../ApplicationContext';
import { getExtendedOfferStatus } from '@n2-common/services/legacyApplicationApiService';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';
import 'rc-slider/assets/index.css';

type SelectYearSliderProps = {
    /** Array of offers */
    offers: Offer[];
    /** Callback gets guid of offer selected via number of terms in slider */
    onChange: (guid?: string) => void;
};

function SelectYearSlider({ offers, onChange }: SelectYearSliderProps) {
    const { t } = useTranslation('common');
    const onChangeRef = useRef(onChange);
    const values = useMemo(() => offers.map((o) => [(o.NumberOfTerms || 0) / 12, o.OfferGuid] as const).filter(([o]) => o), [offers]);

    const range = useMemo(() => {
        const years = values.map(([v]) => v);
        return { min: Math.min(...years), max: Math.max(...years) };
    }, [values]);

    const currentOfferYears = useMemo(() => {
        const current = offers.find((o) => o.IsCurrentOffer);
        if (current && current.NumberOfTerms) {
            return current.NumberOfTerms / 12;
        }
        return range.max;
    }, [offers, range]);

    const [years, setYears] = useState(currentOfferYears);
    const marks = useMemo(
        () => values.reduce((m, [years]) => ({ ...m, [years]: t<string>('AnnuityOffer.SelectTerm.MarkFormat', { years }) }), {}),
        [values, t]
    );

    useEffect(() => onChangeRef.current(values.find(([v]) => v == years)?.[1]), [values, years]);

    return (
        <div className={joinNames(style.selectTermsWrapper, style.rcSlider)}>
            <strong>{t<string>('AnnuityOffer.SelectTerm.Label')}</strong>
            <Slider {...range} value={years} onChange={(val) => setYears(Array.isArray(val) ? val[0] : val)} marks={marks} {...SLIDER_STYLES} />
        </div>
    );
}

type SelectOfferSliderProps = {
    applicationStatus: ApplicationStatusModel | null;
    onError: (err: unknown) => void;
    onOffer?: (offer: Offer) => void;
};

export default function SelectOfferSlider({ applicationStatus, onError, onOffer }: SelectOfferSliderProps) {
    const idCtxt = useApplicationIdContext();

    const onOfferdRef = useRef(onOffer);
    const onErrorRef = useRef(onError);

    const [offerGuid, setOfferGuid] = useState<string>();
    const [offers, setOffers] = useState<Offer[]>([]);

    const offerSelctionApplicable = useMemo(
        () => applicationStatus && applicationStatus.IsAnnuity && typeof onOfferdRef.current == 'function',
        [applicationStatus]
    );

    useEffect(() => {
        const doGetExtendedOfferStatus = async () => {
            try {
                const { Offers } = await getExtendedOfferStatus(idCtxt.applicationId, idCtxt.applicantId);
                setOffers(Offers);
            } catch (err) {
                logErrorToServerside('There was an error getting annuity offers', idCtxt.applicationId, idCtxt.applicantId, err);
                onErrorRef.current(`There was an error getting annuity offers`);
            }
        };

        if (offerSelctionApplicable) {
            doGetExtendedOfferStatus();
        }
    }, [idCtxt, offerSelctionApplicable]);

    const selectedOffer = useMemo(() => offers.find((o) => o.OfferGuid == offerGuid), [offerGuid, offers]);

    useEffect(() => {
        onOfferdRef.current && selectedOffer && onOfferdRef.current(selectedOffer);
    }, [selectedOffer]);

    //If only one offer, select it. We are not showing any slider.
    useEffect(() => {
       if (offers.length === 1 && onOffer) {
        onOffer(offers[0]);
       }
    }, [offers, onOffer]);

    return offerSelctionApplicable && offers.length > 1 ? <SelectYearSlider offers={offers} onChange={setOfferGuid} /> : null;
}

const SLIDER_BORDER_COLOR = '#006f7a';
const SLIDER_STYLES = {
    style: { margin: '12px 0' },
    dotStyle: { borderColor: SLIDER_BORDER_COLOR, bottom: '-7px', width: '12px', height: '12px' },
    activeDotStyle: {},
    railStyle: { height: '10px' },
    trackStyle: { height: '10px', background: 'linear-gradient(180deg, var(--color-400) 0%, var(--color-200) 100%)' },
    handleStyle: { width: '25px', height: '25px', marginTop: '-8px', borderColor: SLIDER_BORDER_COLOR },
};
